<template>
  <div>
    <!-- Header slot for custom titles and actions -->
    <slot name="header">
      <Subheader icon="mdi-history" title="Logs"></Subheader>
    </slot>

    <v-timeline dense align-top>
      <!-- Comment form timeline item -->
      <v-timeline-item
        v-if="showCommentForm"
        fill-dot
        class="mb-2"
        color="white"
        icon-color="grey"
        icon="mdi-pencil"
        small
      >
        <v-card :outlined="outlinedCards">
          <slot
            name="comment-form"
            :commentData="commentFormData"
            :addComment="addComment"
          >
            <v-card-text>
              <v-textarea
                v-model="commentFormData.value"
                dense
                :label="commentFormLabel"
                hide-details="auto"
                outlined
                no-resize
                auto-grow
                rows="3"
              >
              </v-textarea>
              <v-alert
                v-if="commentFormData.errorMessage"
                dense
                type="error"
                text
                class="mt-4 mb-0"
              >
                {{ commentFormData.errorMessage }}
              </v-alert>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <slot
                name="comment-form-actions"
                :commentData="commentFormData"
                :addComment="addComment"
              >
                <v-spacer></v-spacer>
                <v-btn
                  class="mx-0"
                  color="success"
                  depressed
                  :loading="commentFormData.isLoading"
                  :disabled="!commentFormData.value"
                  @click="addComment"
                >
                  {{ commentFormButtonText }}
                </v-btn>
              </slot>
            </v-card-actions>
          </slot>
        </v-card>
      </v-timeline-item>

      <!-- Timeline entries -->
      <v-slide-x-transition group>
        <div v-for="entry in timelineEntries" :key="entry.id" class="mb-2">
          <v-timeline-item
            color="white"
            fill-dot
            small
            :icon-color="getEntryColor(entry)"
            :icon="getEntryIcon(entry)"
            outlined
          >
            <slot
              name="timeline-item"
              :entry="entry"
              :timestampConverter="effectiveTimestampConverter"
            >
              <v-card :outlined="outlinedCards">
                <v-card-text>
                  <v-row justify="space-between">
                    <v-col cols="7">
                      <slot name="timeline-item-content" :entry="entry">
                        <strong>{{ getUserDisplayName(entry) }}</strong>
                        {{ getActionText(entry) }}
                        <span v-if="entry.type === 'comment'">
                          <br />{{ entry.text }}
                        </span>
                        <span v-if="entry.reason">
                          <br />Grund: {{ entry.reason }}
                        </span>
                      </slot>
                    </v-col>
                    <v-col class="text-right" cols="5">
                      <slot
                        name="timeline-item-timestamp"
                        :entry="entry"
                        :timestampConverter="effectiveTimestampConverter"
                      >
                        {{ effectiveTimestampConverter(entry.timestamp) }}
                      </slot>
                    </v-col>
                  </v-row>
                </v-card-text>
                <slot name="timeline-item-actions" :entry="entry">
                  <!-- Optional actions for each timeline item -->
                </slot>
              </v-card>
            </slot>
          </v-timeline-item>
        </div>
      </v-slide-x-transition>

      <slot name="timeline-footer">
        <v-divider></v-divider>
      </slot>
    </v-timeline>
  </div>
</template>

<script>
import Subheader from "@/components/_systemwide/Subheader.vue";

export default {
  name: "LogsCommentsTimeline",
  components: {
    Subheader,
  },
  props: {
    // Timeline data
    timelineEntries: {
      type: Array,
      default: () => [],
      required: true,
    },

    // Comment form configuration
    showCommentForm: {
      type: Boolean,
      default: true,
    },
    commentFormLabel: {
      type: String,
      default: "Kommentar hinzufügen",
    },
    commentFormButtonText: {
      type: String,
      default: "Hinzufügen",
    },

    // Action text mappings
    actionTextMap: {
      type: Object,
      default: () => ({
        created: "hat den Eintrag erstellt.",
        approved: "hat genehmigt.",
        toBeReviewed: "hat zur Prüfung übergeben.",
        rejected: "hat abgelehnt.",
        comment: "hat einen Kommentar hinzugefügt:",
        updatedTrainingData: "hat Ausbildungsdaten aktualisiert.",
        reassigned: "hat die Verantwortung übernommen.",
        reassignedTakeOver: "hat die Verantwortung übernommen.",
        reassignedCorrection: "hat zur Korrektur übernommen.",
        completed: "hat festgeschrieben.",
        paid: "hat die Auszahlung erfasst.",
        archived: "hat archiviert.",
      }),
    },

    // Color mappings
    colorMap: {
      type: Object,
      default: () => ({
        created: "primary",
        approved: "green",
        completed: "green",
        paid: "green",
        toBeReviewed: "purple",
        rejected: "red",
        reassigned: "primary",
        reassignedTakeOver: "primary",
        reassignedCorrection: "primary",
        comment: "grey",
        archived: "grey blue",
      }),
    },

    // Icon mappings
    iconMap: {
      type: Object,
      default: () => ({
        created: "mdi-plus",
        approved: "mdi-check",
        toBeReviewed: "mdi-check-all",
        rejected: "mdi-close",
        reassigned: "mdi-account-switch",
        reassignedTakeOver: "mdi-account-switch",
        reassignedCorrection: "mdi-account-switch",
        comment: "mdi-comment-text",
        completed: "mdi-archive-check",
        paid: "mdi-cash-check",
        archived: "mdi-archive-cancel",
      }),
    },

    // User display name field path
    userDisplayNameField: {
      type: String,
      default: "user.displayName",
    },

    // Custom timestamp converter function
    timestampConverter: {
      type: Function,
      default: null,
    },

    // Card styling
    outlinedCards: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      commentFormData: {
        value: "",
        isLoading: false,
        errorMessage: "",
      },
    };
  },

  methods: {
    getActionText(entry) {
      if (this.actionTextMap[entry.type]) {
        return this.actionTextMap[entry.type];
      }
      return `hat eine Aktion durchgeführt: ${entry.type}`;
    },

    getEntryColor(entry) {
      return this.colorMap[entry.type] || "grey";
    },

    getEntryIcon(entry) {
      return this.iconMap[entry.type] || "mdi-circle";
    },

    getUserDisplayName(entry) {
      // Support nested object paths like "user.displayName"
      const fields = this.userDisplayNameField.split(".");
      let value = entry;
      for (const field of fields) {
        if (value && value[field] !== undefined) {
          value = value[field];
        } else {
          return "Unbekannter Benutzer";
        }
      }
      return value || "Unbekannter Benutzer";
    },

    defaultTimestampConverter(timestamp) {
      if (!timestamp) return "";

      const options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };

      // Handle Firestore Timestamp objects
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString("de-DE", options);
      }

      // Handle regular Date objects
      if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString("de-DE", options);
      }

      // Handle timestamp numbers
      if (typeof timestamp === "number") {
        return new Date(timestamp).toLocaleDateString("de-DE", options);
      }

      return timestamp.toString();
    },

    addComment() {
      if (!this.commentFormData.value.trim()) {
        this.commentFormData.errorMessage = "Kommentar darf nicht leer sein.";
        return;
      }

      this.$emit("add-comment", {
        text: this.commentFormData.value,
        formData: this.commentFormData,
        clearForm: this.clearCommentForm,
      });
    },

    clearCommentForm() {
      this.commentFormData.value = "";
      this.commentFormData.errorMessage = "";
    },

    setCommentFormLoading(loading) {
      this.commentFormData.isLoading = loading;
    },

    setCommentFormError(error) {
      this.commentFormData.errorMessage = error;
    },
  },

  computed: {
    effectiveTimestampConverter() {
      return this.timestampConverter || this.defaultTimestampConverter;
    },
  },

  watch: {
    timelineEntries: {
      handler() {
        // Clear any error messages when new entries are loaded
        this.commentFormData.errorMessage = "";
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
